import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Webhook } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIntercomEvent } from "@/lib/api";

const INTERCOM_STATUS_LABELS: Record<string, string> = {
  received: "Received",
  processing: "Processing",
  processed: "Processed",
  skipped: "Skipped",
  failed: "Failed",
};

function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function DetailSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
        {title}
      </p>
      {subtitle && (
        <p className="text-xs text-muted-foreground mb-2">{subtitle}</p>
      )}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function DetailField({
  label,
  value,
  helper,
  mono,
}: {
  label: string;
  value: string;
  helper?: string;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b pb-2">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="col-span-2">
        <div className={cn("text-sm break-words", mono && "font-mono text-xs")}>
          {value}
        </div>
        {helper && (
          <p className="text-xs text-muted-foreground mt-1">{helper}</p>
        )}
      </dd>
    </div>
  );
}

export interface IntercomEventDetailModalProps {
  eventId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function IntercomEventDetailModal({
  eventId,
  onOpenChange,
}: IntercomEventDetailModalProps) {
  const { data: event, isLoading } = useIntercomEvent(eventId);
  const open = !!eventId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Webhook className="h-4 w-4" />
            Intercom Event{event?.itemId ? ` — ${event.itemId}` : ""}
          </DialogTitle>
          <DialogDescription>
            Raw webhook delivery captured from Intercom.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !event ? (
          <div className="mt-6 space-y-3">
            <div className="h-6 bg-muted animate-pulse rounded" />
            <div className="h-6 bg-muted animate-pulse rounded" />
            <div className="h-64 bg-muted animate-pulse rounded" />
          </div>
        ) : (
          <div className="mt-6 space-y-8">
            <DetailSection title="Webhook">
              <DetailField
                label="Notification ID"
                value={event.notificationId}
                mono
                helper="Intercom's unique webhook delivery id"
              />
              <DetailField
                label="Topic"
                value={event.topic ?? "—"}
                helper="Type of Intercom event"
              />
              <DetailField
                label="Source Type"
                value={event.sourceType ?? "—"}
                helper="Channel that originated the conversation"
              />
            </DetailSection>

            <DetailSection title="Item">
              <DetailField label="Item Type" value={event.itemType ?? "—"} />
              <DetailField
                label="Item ID"
                value={event.itemId ?? "—"}
                mono
                helper="Intercom conversation or ticket id"
              />
              <DetailField
                label="Contact ID"
                value={event.contactId ?? "—"}
                mono
                helper="Intercom contact id of the sender"
              />
            </DetailSection>

            <DetailSection title="Processing">
              <DetailField
                label="Status"
                value={
                  INTERCOM_STATUS_LABELS[event.processingStatus] ??
                  event.processingStatus
                }
                helper="Outcome of the COI pipeline for this event"
              />
              <DetailField
                label="Received at"
                value={formatDate(event.receivedAt)}
              />
              <DetailField
                label="Processed at"
                value={event.processedAt ? formatDate(event.processedAt) : "—"}
              />
              {event.errorMessage && (
                <DetailField
                  label="Error"
                  value={event.errorMessage}
                  helper="Reason this event failed to process"
                />
              )}
            </DetailSection>

            <DetailSection
              title="Event Data"
              subtitle="Raw payload Intercom sent for this event"
            >
              <pre className="rounded-lg bg-muted p-4 text-xs font-mono overflow-auto max-h-[480px] whitespace-pre-wrap break-words">
                {JSON.stringify(event.eventData ?? null, null, 2)}
              </pre>
            </DetailSection>

            <DetailSection title="Metadata">
              <DetailField label="UUID" value={event.id} mono />
            </DetailSection>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default IntercomEventDetailModal;
